import { QuestionCore } from '../types'
import { ALL_QUESTIONS_CORE } from '../data/questionLoader'
import { MODULES } from '../data/modules'
import { getLevelById } from '../config/levels'
import { HINT_REGISTRY } from '../data/hints'
import {
  validateTierConsistency,
  getDifficultyTier,
} from './difficultyTierMapping'

/**
 * JSON Validator v3.0 for CITO Question Data
 * Ensures data integrity for the adaptive engine
 *
 * ============================================================
 * 🔄 MIGRATION STATUS: MIGRATION-AWARE VALIDATION
 * ============================================================
 *
 * v3.0 adds migration readiness tracking:
 * - Reports which questions have explicit v2.0 fields vs derived
 * - Warns about missing conceptId and variantIndex (recommended fields)
 * - Validates consistency between explicit and derived tier values
 * - Tracks overall migration progress per module
 *
 * Validation is NON-BLOCKING: missing new fields produce warnings,
 * not errors. The system remains fully functional with derived values.
 *
 * See: src/docs/FAMILY_ID_MIGRATION.md for full specification
 * ============================================================
 */

export interface MigrationReadiness {
  /** Total questions in the system */
  totalQuestions: number
  /** Questions with explicit difficultyTier in JSON (not derived) */
  withExplicitTier: number
  /** Questions with conceptId */
  withConceptId: number
  /** Questions with variantIndex */
  withVariantIndex: number
  /** Questions with explicit levelCode in JSON */
  withExplicitLevelCode: number
  /** Questions with explicit group in JSON */
  withExplicitGroup: number
  /** Questions missing familyId entirely */
  missingFamilyId: number
  /** Questions where explicit tier doesn't match derived tier */
  tierMismatches: { id: string; explicit: number; derived: number }[]
  /** Migration progress per module (percentage of fully migrated questions) */
  byModule: Record<
    string,
    {
      total: number
      fullyMigrated: number
      percentComplete: number
    }
  >
  /** Overall migration percentage (questions with all required new fields) */
  overallPercent: number
}

export interface ValidationReport {
  totalQuestions: number
  orphanedQuestions: string[]
  missingFields: {
    id: string
    fields: string[]
  }[]
  invalidDifficulty: {
    id: string
    difficulty: number
  }[]
  unmappedLevels: {
    id: string
    difficulty: number
  }[]
  missingExplanations: string[]
  missingHintCoverage: {
    moduleId: string
    subSkill: string
    questionCount: number
  }[]
  duplicateIds: string[]
  moduleStats: Record<string, number>
  /** 🔄 MIGRATION: Readiness report for v2.0 schema migration */
  migrationReadiness: MigrationReadiness
}

export function validateQuestionData(): ValidationReport {
  const report: ValidationReport = {
    totalQuestions: ALL_QUESTIONS_CORE.length,
    orphanedQuestions: [],
    missingFields: [],
    invalidDifficulty: [],
    unmappedLevels: [],
    missingExplanations: [],
    missingHintCoverage: [],
    duplicateIds: [],
    moduleStats: {},
    migrationReadiness: {
      totalQuestions: ALL_QUESTIONS_CORE.length,
      withExplicitTier: 0,
      withConceptId: 0,
      withVariantIndex: 0,
      withExplicitLevelCode: 0,
      withExplicitGroup: 0,
      missingFamilyId: 0,
      tierMismatches: [],
      byModule: {},
      overallPercent: 0,
    },
  }

  const idSet = new Set<string>()
  // Track subSkills per module for hint coverage check
  const subSkillsByModule = new Map<string, Map<string, number>>()

  ALL_QUESTIONS_CORE.forEach((q) => {
    // Check duplicates
    if (idSet.has(q.id)) {
      report.duplicateIds.push(q.id)
    }
    idSet.add(q.id)

    // Track module stats
    report.moduleStats[q.module] = (report.moduleStats[q.module] || 0) + 1

    // Track subSkills per module
    if (q.subSkill) {
      if (!subSkillsByModule.has(q.module)) {
        subSkillsByModule.set(q.module, new Map())
      }
      const moduleSubSkills = subSkillsByModule.get(q.module)!
      moduleSubSkills.set(
        q.subSkill,
        (moduleSubSkills.get(q.subSkill) || 0) + 1,
      )
    }

    // Check required fields
    const missing: string[] = []
    if (!q.module) missing.push('module')
    if (!q.skill) missing.push('skill')
    if (!q.subSkill) missing.push('subSkill')
    if (q.difficulty === undefined) missing.push('difficulty')
    if (q.correctAnswer === undefined) missing.push('correctAnswer')

    if (missing.length > 0) {
      report.missingFields.push({ id: q.id, fields: missing })
    }

    // Check difficulty validity (should be > 0)
    if (q.difficulty <= 0) {
      report.invalidDifficulty.push({ id: q.id, difficulty: q.difficulty })
    }

    // Check if difficulty maps to a valid level config
    if (q.difficulty !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level) {
        report.unmappedLevels.push({ id: q.id, difficulty: q.difficulty })
      }
    }

    // Check if module exists in config
    const validModule = MODULES.find((m) => m.id === q.module)
    if (!validModule) {
      report.orphanedQuestions.push(q.id)
    }

    // ============================================================
    // 🔄 MIGRATION: Track v2.0 schema migration readiness
    // ============================================================

    if (!q.familyId) {
      report.migrationReadiness.missingFamilyId++
    }

    // Check for explicit (non-derived) new fields
    // Note: During migration, difficultyTier/levelCode/group are auto-derived
    // by the loader. We track whether the JSON has explicit values by checking
    // if conceptId exists (which cannot be derived) as a proxy for "fully migrated".
    // A question is "fully migrated" when it has all v2.0 fields explicitly set.
    if (q.difficultyTier !== undefined) {
      report.migrationReadiness.withExplicitTier++

      // Validate tier consistency
      if (!validateTierConsistency(q.difficulty, q.difficultyTier)) {
        const derivedTier = getDifficultyTier(q.difficulty)
        if (derivedTier !== undefined) {
          report.migrationReadiness.tierMismatches.push({
            id: q.id,
            explicit: q.difficultyTier,
            derived: derivedTier,
          })
        }
      }
    }
    if (q.conceptId) report.migrationReadiness.withConceptId++
    if (q.variantIndex !== undefined)
      report.migrationReadiness.withVariantIndex++
    if (q.levelCode) report.migrationReadiness.withExplicitLevelCode++
    if (q.group !== undefined) report.migrationReadiness.withExplicitGroup++

    // Track per-module migration progress
    if (!report.migrationReadiness.byModule[q.module]) {
      report.migrationReadiness.byModule[q.module] = {
        total: 0,
        fullyMigrated: 0,
        percentComplete: 0,
      }
    }
    const moduleProgress = report.migrationReadiness.byModule[q.module]
    moduleProgress.total++

    // A question is "fully migrated" when it has ALL v2.0 required fields
    // explicitly set in the JSON (not just derived):
    // familyId + difficultyTier + conceptId + variantIndex
    const isFullyMigrated = !!(
      (q.familyId && q.conceptId && q.variantIndex !== undefined)
      // Note: difficultyTier, levelCode, group are auto-derived so we don't
      // require them explicitly. conceptId and variantIndex CANNOT be derived.
    )
    if (isFullyMigrated) {
      moduleProgress.fullyMigrated++
    }
  })

  // Calculate per-module percentages
  Object.values(report.migrationReadiness.byModule).forEach((mod) => {
    mod.percentComplete =
      mod.total > 0 ? Math.round((mod.fullyMigrated / mod.total) * 100) : 0
  })

  // Calculate overall migration percentage
  const totalFullyMigrated = Object.values(
    report.migrationReadiness.byModule,
  ).reduce((sum, mod) => sum + mod.fullyMigrated, 0)
  report.migrationReadiness.overallPercent =
    report.migrationReadiness.totalQuestions > 0
      ? Math.round(
          (totalFullyMigrated / report.migrationReadiness.totalQuestions) * 100,
        )
      : 0

  // Check hint coverage: find subSkills that have questions but no hints
  subSkillsByModule.forEach((subSkills, moduleId) => {
    const moduleHints = HINT_REGISTRY[moduleId as keyof typeof HINT_REGISTRY]
    subSkills.forEach((count, subSkill) => {
      if (!moduleHints || !moduleHints[subSkill]) {
        // Check partial match (same logic as getHintsForSubSkill)
        const hasPartialMatch =
          moduleHints &&
          Object.keys(moduleHints).some(
            (k) => subSkill.includes(k) || k.includes(subSkill),
          )
        if (!hasPartialMatch) {
          report.missingHintCoverage.push({
            moduleId,
            subSkill,
            questionCount: count,
          })
        }
      }
    })
  })

  return report
}
